import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';

import { computeHoursStatus, deriveShareMoments, type Review, type Ticket } from '@kilat/shared';
import {
  Button,
  EmptyState,
  eyebrowStyle,
  ghostLinkStyle,
  linkRowStyle,
  Skeleton,
  srOnly,
  Surface,
} from '@kilat/ui';

import { useAuth } from '../lib/use-auth.js';
import { useActiveOutlet } from '../lib/use-active-outlet.js';
import { useI18n } from '../lib/use-i18n.js';
import { useTrackedSnapshot } from '../lib/use-tracked-snapshot.js';
import { logger } from '../lib/logger.js';
import { dashboardPageStyle, sectionGridStyle } from '../lib/layout.js';
import { tenantsRepo } from '../lib/repositories/tenants-repo.js';
import { brandingRepoFor } from '../lib/repositories/branding-repo.js';
import { promotionsRepoFor } from '../lib/repositories/promotions-repo.js';
import { lifetimeStatsRepoFor } from '../lib/repositories/lifetime-stats-repo.js';
import { LOYALTY_CONFIG_DOC_ID, loyaltyConfigRepoFor } from '../lib/repositories/loyalty-repo.js';
import { reviewsRepoFor } from '../lib/repositories/reviews-repo.js';
import { ticketsRepoFor } from '../lib/repositories/outlet-repos.js';
import { mutedText, ShareKit, type KitData } from '../components/share-kit.js';

/**
 * /marketing/share — the operator Share kit (feature doc
 * `docs/features/share-and-grow.feature.md`).
 *
 * Turns what is really happening at the carwash into a post the operator can
 * send in a tap: a caption in the language they choose (editable, optionally
 * polished by AI), a branded image drawn in the browser, and a link that tells
 * Reports which channel brought each booking in.
 *
 * NEVER FABRICATED. Every card on the left comes from `deriveShareMoments`,
 * which only returns a moment backed by a record: an active promotion, a wash
 * milestone the lifetime counter actually reached, today's real hours and
 * queue, the referral programme if it is on, a real five-star review (its
 * text only — no customer name, no photo, ever).
 *
 * NOTHING IS AUTO-POSTED. Each share opens the channel's own composer, or
 * downloads the image for the apps that have no web composer (Instagram,
 * TikTok). Posting on the operator's behalf needs per-operator OAuth and
 * Meta / TikTok / Google app review — a future tier, not this one.
 *
 * Reads are bounded and scoped to this screen: five one-shot document reads,
 * plus two capped listeners (20 recent reviews, 20 waiting tickets) that close
 * when the operator leaves.
 */

type LoadState =
  | { readonly kind: 'loading' }
  | { readonly kind: 'error' }
  | { readonly kind: 'ready'; readonly data: KitData };

const RECENT_REVIEWS = 20;
const WAITING_CAP = 20;

const tsMs = (t: { readonly seconds: number; readonly nanoseconds: number }) =>
  t.seconds * 1000 + Math.floor(t.nanoseconds / 1e6);

export function ShareKitPage() {
  const { t } = useI18n();
  const { claims } = useAuth();
  const tenantId =
    typeof claims?.['defaultTenant'] === 'string' ? (claims['defaultTenant'] as string) : null;
  const { outletId } = useActiveOutlet();
  const [state, setState] = useState<LoadState>({ kind: 'loading' });
  const [reload, setReload] = useState(0);

  useEffect(() => {
    if (!tenantId) return;
    let cancelled = false;
    setState({ kind: 'loading' });
    void (async () => {
      const [tenant, branding, promotions, lifetime, loyalty] = await Promise.allSettled([
        tenantsRepo.get(tenantId),
        brandingRepoFor(tenantId).get('current'),
        promotionsRepoFor(tenantId).list(),
        lifetimeStatsRepoFor(tenantId).get('lifetime'),
        loyaltyConfigRepoFor(tenantId).get(LOYALTY_CONFIG_DOC_ID),
      ]);
      if (cancelled) return;
      if (
        tenant.status !== 'fulfilled' ||
        !tenant.value ||
        branding.status !== 'fulfilled' ||
        !branding.value
      ) {
        logger.error('share kit: tenant or branding unavailable', {
          tenant: tenant.status,
          branding: branding.status,
        });
        setState({ kind: 'error' });
        return;
      }
      // The optional sources degrade to "no moment of that kind" — a missing
      // promotion list must not hide today's hours.
      for (const [name, r] of [
        ['promotions', promotions],
        ['lifetime', lifetime],
        ['loyalty', loyalty],
      ] as const) {
        if (r.status === 'rejected') {
          logger.warn('share kit: optional source failed', { name, error: String(r.reason) });
        }
      }
      setState({
        kind: 'ready',
        data: {
          profile: tenant.value.profile,
          branding: branding.value,
          promotions:
            promotions.status === 'fulfilled'
              ? promotions.value.map((p) => ({
                  id: p.id,
                  name: p.name,
                  status: p.status,
                  validFromMs: tsMs(p.validity.from),
                  validToMs: tsMs(p.validity.to),
                  effect: p.effect,
                }))
              : [],
          washCount: lifetime.status === 'fulfilled' ? lifetime.value?.washCount : undefined,
          referralsEnabled:
            loyalty.status === 'fulfilled' && loyalty.value?.enabledFeatures.referrals === true,
        },
      });
    })();
    return () => {
      cancelled = true;
    };
  }, [tenantId, reload]);

  const reviewsSub = useMemo(() => {
    if (!tenantId || !outletId) return null;
    const repo = reviewsRepoFor(tenantId, outletId);
    return (cb: (v: Review[]) => void) =>
      repo.listSnapshotBounded({ orderBy: [['createdAt', 'desc']], limitTo: RECENT_REVIEWS }, cb);
  }, [tenantId, outletId]);
  const reviews = useTrackedSnapshot<Review[]>(['share-reviews', tenantId, outletId], reviewsSub);

  const waitingSub = useMemo(() => {
    if (!tenantId || !outletId) return null;
    const repo = ticketsRepoFor(tenantId, outletId);
    return (cb: (v: Ticket[]) => void) =>
      repo.listSnapshotBounded({ where: [['status', '==', 'inQueue']], limitTo: WAITING_CAP }, cb);
  }, [tenantId, outletId]);
  const waiting = useTrackedSnapshot<Ticket[]>(['share-waiting', tenantId, outletId], waitingSub);

  if (!tenantId) {
    return (
      <Shell>
        <Surface>
          <EmptyState
            title={t('dashboard.shareKit.noTenantTitle')}
            body={t('dashboard.shareKit.noTenantBody')}
          />
        </Surface>
      </Shell>
    );
  }

  if (state.kind === 'loading') {
    return (
      <Shell>
        <div role="status" aria-busy="true" style={sectionGridStyle}>
          <span style={srOnly}>{t('dashboard.shareKit.loading')}</span>
          <Skeleton height={320} radius={16} />
          <Skeleton height={320} radius={16} />
        </div>
      </Shell>
    );
  }

  if (state.kind === 'error') {
    return (
      <Shell>
        <Surface accent="danger">
          <EmptyState
            title={t('dashboard.shareKit.errorTitle')}
            body={t('dashboard.shareKit.errorBody')}
            action={
              <Button variant="primary" onClick={() => setReload((n) => n + 1)}>
                {t('dashboard.shareKit.retry')}
              </Button>
            }
          />
        </Surface>
      </Shell>
    );
  }

  const { data } = state;
  const moments = deriveShareMoments({
    nowMs: Date.now(),
    promotions: data.promotions,
    washCount: data.washCount,
    hoursStatus: computeHoursStatus(data.branding.hours, new Date()),
    // Unknown until the listener answers — "open", never a guessed "quiet".
    waitingCount: waiting.loading || waiting.error ? null : (waiting.data ?? []).length,
    referralsEnabled: data.referralsEnabled,
    reviews: (reviews.data ?? []).map((r) => ({
      rating: r.rating,
      text: r.text,
      hidden: r.hidden,
      createdAtMs: tsMs(r.createdAt),
    })),
  });

  return (
    <Shell>
      <GoogleReviewNote googleReview={data.branding.contact.googleReview} />
      {moments.length === 0 ? (
        <Surface>
          <EmptyState
            title={t('dashboard.shareKit.emptyTitle')}
            body={t('dashboard.shareKit.emptyBody')}
            action={
              <div style={linkRowStyle}>
                <Link to="/loyalty/promotions" style={ghostLinkStyle}>
                  {t('dashboard.shareKit.emptyPromo')}
                </Link>
                <Link to="/branding/hours" style={ghostLinkStyle}>
                  {t('dashboard.shareKit.emptyHours')}
                </Link>
              </div>
            }
          />
        </Surface>
      ) : (
        <ShareKit tenantId={tenantId} data={data} moments={moments} />
      )}
    </Shell>
  );
}

function Shell({ children }: { readonly children: ReactNode }) {
  const { t } = useI18n();
  return (
    <main style={dashboardPageStyle}>
      <div style={{ padding: '0 var(--space-5)', display: 'grid', gap: 'var(--space-5)' }}>
        <header style={{ display: 'grid', gap: 'var(--space-2)' }}>
          <p style={eyebrowStyle}>{t('dashboard.shareKit.eyebrow')}</p>
          <h1 style={{ margin: 0, fontSize: 'var(--text-h1)', fontWeight: 650 }}>
            {t('dashboard.shareKit.title')}
          </h1>
          <p style={{ ...mutedText, maxWidth: '68ch' }}>{t('dashboard.shareKit.subtitle')}</p>
          <p style={{ ...mutedText, maxWidth: '68ch' }}>{t('dashboard.shareKit.notAutoPost')}</p>
        </header>
        {children}
      </div>
    </main>
  );
}

function GoogleReviewNote({ googleReview }: { readonly googleReview: string | undefined }) {
  const { t } = useI18n();
  return (
    <Surface level={0} accent={googleReview ? 'success' : 'info'}>
      <div style={{ ...linkRowStyle, justifyContent: 'space-between', alignItems: 'center' }}>
        <div style={{ display: 'grid', gap: 'var(--space-1)', minWidth: 0, flex: '1 1 20rem' }}>
          <strong>{t('dashboard.shareKit.googleTitle')}</strong>
          <span style={mutedText}>
            {googleReview
              ? t('dashboard.shareKit.googleSet')
              : t('dashboard.shareKit.googleMissing')}
          </span>
        </div>
        <Link to="/branding/hours#links" style={ghostLinkStyle}>
          {googleReview ? t('dashboard.shareKit.googleEdit') : t('dashboard.shareKit.googleAction')}
        </Link>
      </div>
    </Surface>
  );
}
